import styles from './InformesResumenPage.module.css';
import stylesPanel from '../../../../styles/Panel.module.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import HeaderTituloComponent from '../../../../components/header-titulo/HeaderTituloComponent';
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { srvReportesEncuestasCreadas } from '../../../../services/informes/srvReportesEncuestasCreadas';
import { srvReportesEncuestaCompletadas } from '../../../../services/informes/srvReportesEncuestaCompletadas';
import { srvBuscarTodasLasEncuestas } from '../../../../services/encuestas/srvBuscarTodasLasEncuestas';

const DEFAULT_TIMEZONE = 'America/Santiago';
const DEFAULT_AGRUPACION = 'mes';

const getDefaultReportParams = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return {
        from: `${currentYear}-01`,
        to: today,
        agrupacion: DEFAULT_AGRUPACION,
        zonaHoraria: DEFAULT_TIMEZONE
    };
};

const mapCreationSeries = (rows = []) =>
    rows.map((item) => ({
        label: item?.periodo ?? '',
        value: Number(item?.total ?? 0),
        periodoInicio: item?.periodoInicio ?? null,
        periodoFin: item?.periodoFin ?? null
    }));

const mapCompletionsSeries = (rows = []) =>
    rows.map((item) => ({
        label: item?.periodo ?? '',
        value: Number(item?.completadas ?? item?.total ?? 0),
        periodoInicio: item?.periodoInicio ?? null,
        periodoFin: item?.periodoFin ?? null
    }));

const deriveRangeFromSeries = (series = []) => {
    if (!series.length) {
        return null;
    }
    const first = series[0];
    const last = series[series.length - 1];
    return {
        from: first?.periodoInicio ?? first?.label ?? null,
        to: last?.periodoFin ?? last?.label ?? null
    };
};

const normalizeRange = (range, fallbackRange) => {
    if (range && typeof range === 'object' && !Array.isArray(range)) {
        const from =
            range.desde ??
            range.from ??
            range.fechaDesde ??
            range.inicio ??
            range.periodoInicio ??
            range.start ??
            range.startDate ??
            null;
        const to =
            range.hasta ??
            range.to ??
            range.fechaHasta ??
            range.fin ??
            range.periodoFin ??
            range.end ??
            range.endDate ??
            null;
        if (from || to) {
            return { from, to };
        }
    }
    return fallbackRange || null;
};

const formatRangeLabel = (range) => {
    if (!range) {
        return 'Rango no disponible';
    }
    const from = range.from ?? range.desde ?? range.inicio ?? null;
    const to = range.to ?? range.hasta ?? range.fin ?? null;
    if (!from && !to) {
        return 'Rango no disponible';
    }
    return `${from ?? '?'} → ${to ?? '?'}`;
};

const getSurveyLabel = (surveyMeta, options, id) => {
    if (surveyMeta?.titulo) {
        return surveyMeta.titulo;
    }
    const option = options.find((item) => item.id === id);
    return option?.label || 'Encuesta';
};

const InformesResumenPage = () => {
    const userData = useSelector((state) => state.authInfo);
    const defaultParams = useMemo(() => getDefaultReportParams(), []);
    const canRequest = Boolean(userData?.usuario?.token);

    const [creationSeries, setCreationSeries] = useState([]);
    const [creationMeta, setCreationMeta] = useState({ total: 0, range: null, timezone: DEFAULT_TIMEZONE });
    const [creationLoading, setCreationLoading] = useState(false);
    const [creationError, setCreationError] = useState(null);

    const [surveyOptions, setSurveyOptions] = useState([]);
    const [surveysLoading, setSurveysLoading] = useState(false);
    const [surveysError, setSurveysError] = useState(null);

    const [selectedSurveyId, setSelectedSurveyId] = useState(null);

    const [completionsSeries, setCompletionsSeries] = useState([]);
    const [completionsMeta, setCompletionsMeta] = useState({
        total: 0,
        range: null,
        timezone: DEFAULT_TIMEZONE,
        survey: null
    });
    const [completionsLoading, setCompletionsLoading] = useState(false);
    const [completionsError, setCompletionsError] = useState(null);

    const completionsCache = useRef({});

    const formatNumber = (value) => Number(value ?? 0).toLocaleString('es-CL');

    useEffect(() => {
        if (!canRequest) {
            setCreationSeries([]);
            setCreationMeta({ total: 0, range: null, timezone: DEFAULT_TIMEZONE });
            return;
        }

        let isMounted = true;
        setCreationLoading(true);
        setCreationError(null);

        srvReportesEncuestasCreadas(userData, defaultParams)
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                const series = mapCreationSeries(data?.datos);
                const range = normalizeRange(data?.rango ?? data?.rangoAplicado, deriveRangeFromSeries(series));
                setCreationSeries(series);
                setCreationMeta({
                    total: Number(data?.totalGeneral ?? 0),
                    range,
                    timezone: data?.zonaHoraria || defaultParams.zonaHoraria || DEFAULT_TIMEZONE
                });
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                setCreationError(error?.message || 'No fue posible cargar el informe.');
                setCreationSeries([]);
                setCreationMeta({ total: 0, range: null, timezone: defaultParams.zonaHoraria || DEFAULT_TIMEZONE });
            })
            .finally(() => {
                if (isMounted) {
                    setCreationLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [canRequest, userData, defaultParams]);

    useEffect(() => {
        if (!canRequest) {
            setSurveyOptions([]);
            setSelectedSurveyId(null);
            return;
        }

        let isMounted = true;
        setSurveysLoading(true);
        setSurveysError(null);

        const fetchSurveys = async () => {
            try {
                const response = await srvBuscarTodasLasEncuestas(userData);
                if (!isMounted) {
                    return;
                }
                const raw = Array.isArray(response?.data) ? response.data : [];
                const unique = new Map();
                raw.forEach((item) => {
                    const id = item?.idEncuestaHash || item?.idEncuesta;
                    if (!id) {
                        return;
                    }
                    const normalizedId = String(id);
                    if (!unique.has(normalizedId)) {
                        unique.set(normalizedId, {
                            id: normalizedId,
                            label: item?.titulo || item?.nombre || `Encuesta ${unique.size + 1}`
                        });
                    }
                });
                const options = Array.from(unique.values()).sort((a, b) =>
                    a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })
                );
                const validIds = new Set(options.map((option) => option.id));
                completionsCache.current = Object.fromEntries(
                    Object.entries(completionsCache.current).filter(([id]) => validIds.has(id))
                );
                setSurveyOptions(options);
                setSelectedSurveyId((current) => {
                    if (current && options.some((option) => option.id === current)) {
                        return current;
                    }
                    return options.length ? options[0].id : null;
                });
            } catch (error) {
                if (!isMounted) {
                    return;
                }
                setSurveysError(error?.message || 'No fue posible obtener las encuestas disponibles.');
                setSurveyOptions([]);
                setSelectedSurveyId(null);
            } finally {
                if (isMounted) {
                    setSurveysLoading(false);
                }
            }
        };

        fetchSurveys();

        return () => {
            isMounted = false;
        };
    }, [canRequest, userData]);

    useEffect(() => {
        if (!canRequest || !selectedSurveyId) {
            setCompletionsSeries([]);
            setCompletionsMeta({ total: 0, range: null, timezone: DEFAULT_TIMEZONE, survey: null });
            setCompletionsLoading(false);
            return;
        }

        const cached = completionsCache.current[selectedSurveyId];
        if (cached) {
            setCompletionsSeries(cached.series);
            setCompletionsMeta(cached.meta);
            setCompletionsError(null);
            setCompletionsLoading(false);
            return;
        }

        let isMounted = true;
        setCompletionsLoading(true);
        setCompletionsError(null);

        srvReportesEncuestaCompletadas(userData, selectedSurveyId, defaultParams)
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                const series = mapCompletionsSeries(data?.datos);
                const range = normalizeRange(data?.rango ?? data?.rangoAplicado, deriveRangeFromSeries(series));
                const surveyMeta = data?.encuesta ?? { idEncuestaHash: selectedSurveyId };
                if (!surveyMeta.titulo) {
                    const fallbackOption = surveyOptions.find((option) => option.id === selectedSurveyId);
                    if (fallbackOption) {
                        surveyMeta.titulo = fallbackOption.label;
                    }
                }
                const meta = {
                    total: Number(data?.totalGeneral ?? 0),
                    range,
                    timezone: data?.zonaHoraria || defaultParams.zonaHoraria || DEFAULT_TIMEZONE,
                    survey: surveyMeta
                };
                completionsCache.current[selectedSurveyId] = { series, meta };
                setCompletionsSeries(series);
                setCompletionsMeta(meta);
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                setCompletionsError(error?.message || 'No fue posible cargar el informe.');
                setCompletionsSeries([]);
                setCompletionsMeta({
                    total: 0,
                    range: null,
                    timezone: defaultParams.zonaHoraria || DEFAULT_TIMEZONE,
                    survey: null
                });
            })
            .finally(() => {
                if (isMounted) {
                    setCompletionsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [canRequest, defaultParams, selectedSurveyId, surveyOptions, userData]);

    const creationAverage =
        creationSeries.length > 0 ? Math.round(creationMeta.total / creationSeries.length) : 0;
    const completionsAverage =
        completionsSeries.length > 0 ? Math.round(completionsMeta.total / completionsSeries.length) : 0;

    const selectedSurveyLabel = getSurveyLabel(completionsMeta.survey, surveyOptions, selectedSurveyId);

    return (
        <div className={stylesPanel.containerPrincipal}>
            <HeaderTituloComponent pagina={HeaderTituloComponent.PAGINA_INFORMES_RESUMEN}></HeaderTituloComponent>
            <div className={styles.reportLayout}>
                <section className={styles.reportCard}>
                    <div className={styles.cardHeader}>
                        <div>
                            <h2 className={styles.sectionTitle}>Encuestas creadas</h2>
                            <p className={styles.sectionSubtitle}>
                                Seguimiento del ritmo de creación de encuestas dentro del rango solicitado.
                            </p>
                        </div>
                        <div className={styles.metricsGroup}>
                            <div className={styles.metricBubble}>
                                <span className={styles.metricLabel}>Total periodo</span>
                                <span className={styles.metricValue}>{formatNumber(creationMeta.total)}</span>
                            </div>
                            <div className={styles.metricBubble}>
                                <span className={styles.metricLabel}>Prom. mensual</span>
                                <span className={styles.metricValue}>{formatNumber(creationAverage)}</span>
                            </div>
                        </div>
                    </div>
                    {(creationMeta.range || creationMeta.timezone) && (
                        <div className={styles.metaInfo}>
                            {creationMeta.range && (
                                <span className={styles.metaInfoItem}>Rango: {formatRangeLabel(creationMeta.range)}</span>
                            )}
                            {creationMeta.timezone && (
                                <span className={styles.metaInfoItem}>Zona horaria: {creationMeta.timezone}</span>
                            )}
                        </div>
                    )}
                    <div className={styles.chartContainer}>
                        {creationLoading ? (
                            <div className={styles.feedback}>Cargando datos...</div>
                        ) : creationError ? (
                            <div className={`${styles.feedback} ${styles.feedbackError}`}>{creationError}</div>
                        ) : creationSeries.length ? (
                            <ResponsiveContainer width="100%" height={320}>
                                <AreaChart data={creationSeries} margin={{ top: 12, right: 16, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.7} />
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid stroke="#e5e7eb" strokeDasharray="4 4" vertical={false} />
                                    <XAxis
                                        dataKey="label"
                                        tick={{ fill: '#6b7280', fontSize: 12 }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fill: '#6b7280', fontSize: 12 }}
                                        axisLine={false}
                                        tickLine={false}
                                        allowDecimals={false}
                                    />
                                    <Tooltip
                                        formatter={(value, _name, { payload }) => [
                                            `${formatNumber(value)} encuestas`,
                                            payload?.label || 'Periodo'
                                        ]}
                                        contentStyle={{
                                            borderRadius: 12,
                                            borderColor: '#e5e7eb',
                                            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)'
                                        }}
                                        labelStyle={{ color: '#0f172a', fontWeight: 600 }}
                                    />
                                    <Area type="monotone" dataKey="value" stroke="#10b981" strokeWidth={3} fill="url(#colorCreated)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className={styles.feedback}>No hay datos para el rango seleccionado.</div>
                        )}
                    </div>
                </section>

                <section className={styles.reportCard}>
                    <div className={styles.cardHeader}>
                        <div>
                            <h2 className={styles.sectionTitle}>Encuestas completadas</h2>
                            <p className={styles.sectionSubtitle}>
                                Selecciona una encuesta para revisar cómo evolucionan las respuestas completadas con el tiempo.
                            </p>
                        </div>
                        <div className={styles.dropdownContainer}>
                            <label className={styles.dropdownLabel} htmlFor="surveySelector">
                                Encuesta
                            </label>
                            <select
                                id="surveySelector"
                                className={styles.dropdown}
                                value={selectedSurveyId ?? ''}
                                onChange={(event) => setSelectedSurveyId(event.target.value || null)}
                                disabled={!surveyOptions.length || surveysLoading}
                            >
                                {!surveyOptions.length ? (
                                    <option value="">Sin encuestas disponibles</option>
                                ) : (
                                    surveyOptions.map((survey) => (
                                        <option key={survey.id} value={survey.id}>
                                            {survey.label}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                    </div>
                    {surveysError && <p className={styles.errorText}>{surveysError}</p>}
                    <div className={styles.metricsGroup}>
                        <div className={`${styles.metricBubble} ${styles.metricBubbleAlt}`}>
                            <span className={styles.metricLabel}>Total completadas</span>
                            <span className={styles.metricValue}>{formatNumber(completionsMeta.total)}</span>
                        </div>
                        <div className={`${styles.metricBubble} ${styles.metricBubbleAlt}`}>
                            <span className={styles.metricLabel}>Prom. mensual</span>
                            <span className={styles.metricValue}>{formatNumber(completionsAverage)}</span>
                        </div>
                    </div>
                    {(selectedSurveyLabel || completionsMeta.range || completionsMeta.timezone) && (
                        <div className={styles.metaInfo}>
                            {selectedSurveyLabel && (
                                <span className={styles.metaInfoItem}>Encuesta: {selectedSurveyLabel}</span>
                            )}
                            {completionsMeta.range && (
                                <span className={styles.metaInfoItem}>
                                    Rango: {formatRangeLabel(completionsMeta.range)}
                                </span>
                            )}
                            {completionsMeta.timezone && (
                                <span className={styles.metaInfoItem}>Zona horaria: {completionsMeta.timezone}</span>
                            )}
                        </div>
                    )}
                    <div className={styles.chartContainer}>
                        {completionsLoading ? (
                            <div className={styles.feedback}>Cargando datos...</div>
                        ) : completionsError ? (
                            <div className={`${styles.feedback} ${styles.feedbackError}`}>{completionsError}</div>
                        ) : completionsSeries.length ? (
                            <ResponsiveContainer width="100%" height={320}>
                                <LineChart data={completionsSeries} margin={{ top: 12, right: 16, left: -20, bottom: 0 }}>
                                    <CartesianGrid stroke="#e5e7eb" strokeDasharray="4 4" vertical={false} />
                                    <XAxis
                                        dataKey="label"
                                        tick={{ fill: '#6b7280', fontSize: 12 }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fill: '#6b7280', fontSize: 12 }}
                                        axisLine={false}
                                        tickLine={false}
                                        allowDecimals={false}
                                    />
                                    <Tooltip
                                        formatter={(value, _name, { payload }) => [
                                            `${formatNumber(value)} respuestas`,
                                            payload?.label || selectedSurveyLabel
                                        ]}
                                        contentStyle={{
                                            borderRadius: 12,
                                            borderColor: '#e5e7eb',
                                            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)'
                                        }}
                                        labelStyle={{ color: '#0f172a', fontWeight: 600 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke="#2563eb"
                                        strokeWidth={3}
                                        dot={{ r: 4, strokeWidth: 2, stroke: '#2563eb', fill: '#ffffff' }}
                                        activeDot={{ r: 6 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className={styles.feedback}>
                                {selectedSurveyId
                                    ? 'No hay datos disponibles para la encuesta seleccionada.'
                                    : 'Selecciona una encuesta para visualizar sus datos.'}
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default InformesResumenPage;
